// Shopify's CarrierService rate-request payload carries no discount data,
// the only channel we control into it is ach line item's `properties`.
// This file keeps a couple of "estimated discount" properties on every
// cart line up to date in the background.
let cartDiscountSyncPromise = null;
let cartDiscountSyncRerunQueued = false;

// Freebie/addon apps (e.g. bonus-item apps) mutate the cart independently and
// fire their own cart:updated events. Without this guard, an overlapping
// invocation snapshots /cart.js while a prior invocation is still mid-write;
// once the prior write lands, the line item key it touched changes server-side,
// so the overlapping invocation's queued /cart/change.js call references a
// line id that no longer exists (400 "no valid id or line parameter"). Callers
// all await the same in-flight run so a concurrent trigger still resolves
// once the cart is actually settled.
async function syncCartDiscountProperties(cartOverride) {
  if (cartDiscountSyncPromise) {
    cartDiscountSyncRerunQueued = true;
    return cartDiscountSyncPromise;
  }
  cartDiscountSyncPromise = (async () => {
    try {
      await syncCartDiscountPropertiesOnce(cartOverride);
    } finally {
      cartDiscountSyncPromise = null;
      if (cartDiscountSyncRerunQueued) {
        cartDiscountSyncRerunQueued = false;
        // A rerun was requested by an event that landed mid-sync, so its
        // (possibly now-stale) cart snapshot isn't trustworthy - always
        // fetch fresh here rather than threading a second override through.
        await syncCartDiscountProperties();
      }
    }
  })();
  return cartDiscountSyncPromise;
}

// A 400/409 on a line write means another script (freebie/addon app) touched
// that line concurrently. Left as a fire-and-forget flag for the *next*
// external cart:updated to pick up, the fix depended on some other event
// happening to occur afterward - if the page navigated away first (e.g.
// straight to checkout), the correction never ran and a losing holder's
// stale _checkoutPageOrderDiscounts was left behind indefinitely, looking
// exactly like two lines holding the order discount at once. Retrying
// in-place instead closes that window: the caller's await doesn't resolve
// until the cart is actually settled (or retries are exhausted), rather than
// racing the user's next navigation.
const MAX_RACE_RETRIES = 3;
const RACE_RETRY_DELAY_MS = 250;

async function syncCartDiscountPropertiesOnce(cartOverride, attempt = 0) {
  // FETCH: Reuse the cart snapshot already carried on a cart:updated event
  // when available (see cart.js's dispatch of cart:updated with
  // detail.cart) instead of doing a redundant /cart.js round trip.
  const cart = cartOverride || (await (await fetch("/cart.js")).json());

  if (!cart.items || cart.items.length === 0) return;

  // HOLDER: the order-level discount string lives on exactly one line
  // rather than being duplicated onto every line (duplicating it meant any
  // shift in the order total invalidated every line at once, forcing a
  // rewrite of the whole cart on nearly every change). Freebie/reward lines
  // are explicitly flagged via the `__freebie` property by the app that
  // grants them; `final_price > 0` is kept as a fallback for freebie apps
  // that don't set it. The holder must also require_shipping - a
  // non-shipping line is never sent to the CarrierService at all, so its
  // properties would never actually be read. Re-picked fresh on every sync
  // (the first eligible line, in cart order) so if the current holder line
  // is removed, the next eligible line in the cart picks it up
  // automatically.
  const isEligibleOrderDiscountHolder = (item) =>
    item.properties?.__freebie !== "true" &&
    item.requires_shipping !== false &&
    (item.final_price || 0) > 0;
  const holderItem =
    cart.items.find(isEligibleOrderDiscountHolder) ||
    cart.items.find((item) => item.requires_shipping !== false) ||
    cart.items[0];
  const holderKey = holderItem.key;

  // ORDER-CODE IDENTIFICATION: an order-wide code (e.g. "30% off
  // everything") is allocated by Shopify per entitled line, the same shape
  // as a genuine product-specific discount - there's no structural flag
  // distinguishing them. The one explicit signal available is the code our
  // own cart attribute sync records: cart.attributes.client_discount_code
  // (see syncCartAttributes-style writers elsewhere in this theme), e.g.
  // "discountcode=SLT152930". Any line-level allocation whose title
  // references that code is treated as order-level - it's summed into the
  // single-line order aggregate below and left out of that line's own
  // _checkoutPageDiscountCodes so it isn't double-counted in two places.
  // Everything else (a real per-product markdown, a different automatic
  // discount) stays itemized on its own line as before.
  const orderDiscountCode = (cart.attributes?.client_discount_code || "")
    .replace(/^discountcode=/i, "")
    .trim()
    .toLowerCase();
  const isOrderLevelAllocation = (alloc) =>
    !!orderDiscountCode &&
    (alloc.discount_application?.title || "")
      .toLowerCase()
      .includes(orderDiscountCode);

  // PARSE: Build the order-level string. It has two kinds of entries:
  //
  //  1. Order-level totals, summed by title into one combined entry each -
  //     a genuine cart_level_discount_applications entry (an "across"
  //     discount, already spread proportionally over every entitled line,
  //     freebies included), plus a shipped line's order-code-matched
  //     line_level_discount_allocations entry (product-specific discounts
  //     on a shipped line stay itemized on that line instead, see below).
  //  2. Non-shipped (requires_shipping === false) lines' own discounts,
  //     summed by title into the same map as (1) - e.g. two different
  //     freebies both titled "Free Bonus Item" end up as one combined
  //     "Free Bonus Item-$X" entry. Read from item.discounts, the one field
  //     Shopify always populates with a line's own total regardless of
  //     allocation method (unlike line_level_discount_allocations, left
  //     empty for "across" discounts). Shopify never includes a
  //     non-shipping line in the CarrierService request at all, so that
  //     line's own properties are never read by the carrier; folding its
  //     discount into the one property the carrier does read is the only
  //     way that value isn't silently dropped, even though Shopify's own
  //     cart total (which we don't control) still reflects it. A title
  //     already counted in (1) is skipped here - an "across" discount
  //     already includes this line's share in its combined total, and
  //     item.discounts would otherwise re-report that same slice under the
  //     same title.

  const orderDiscountTotals = new Map();
  const cartLevelDiscountTitles = new Set();
  for (const alloc of cart.cart_level_discount_applications || []) {
    const name = alloc.discount_application?.title || "Discount";
    cartLevelDiscountTitles.add(name);
    orderDiscountTotals.set(
      name,
      (orderDiscountTotals.get(name) || 0) +
        (alloc.discount_application?.total_allocated_amount ??
          alloc.amount ??
          0),
    );
  }
  for (const item of cart.items) {
    if (item.requires_shipping === false) {
      for (const discount of item.discounts || []) {
        const name = discount.title || "Discount";
        if (!discount.amount || cartLevelDiscountTitles.has(name)) continue;
        orderDiscountTotals.set(
          name,
          (orderDiscountTotals.get(name) || 0) + discount.amount,
        );
      }
      continue;
    }
    for (const alloc of item.line_level_discount_allocations || []) {
      if (!isOrderLevelAllocation(alloc)) continue;
      const name = alloc.discount_application?.title || "Discount";
      orderDiscountTotals.set(
        name,
        (orderDiscountTotals.get(name) || 0) + alloc.amount,
      );
    }
  }
  const orderValuationString = Array.from(orderDiscountTotals.entries())
    .map(([name, amountCents]) => `${name}-$${(amountCents / 100).toFixed(2)}`)
    .sort()
    .join(",");

  const changePromises = [];
  const debugLineProperties = [];

  // EVALUATE: Prorate itemized line flags
  for (let i = 0; i < cart.items.length; i++) {
    const item = cart.items[i];
    const lineKey = item.key;

    const lineAllocations = item.line_level_discount_allocations || [];
    const itemValuationString = lineAllocations
      .filter((alloc) => !isOrderLevelAllocation(alloc))
      .map((alloc) => {
        const name = alloc.discount_application?.title || "Discount";
        // Per-unit (x1) amount, not the line total: the line total scales
        // with quantity, which makes this property differ between an x1 and
        // x2 line for the same variant and stops Shopify from merging them
        // into one cart row when more is added. This is an estimate for
        // display, so a rounding difference of a cent or two across
        // quantities is acceptable.
        const amount = (alloc.amount / item.quantity / 100).toFixed(2);
        return `${name}-$${amount}`;
      })
      .sort()
      .join(",");

    const desiredOrderCodes = lineKey === holderKey ? orderValuationString : "";

    const existingLineCodes = item.properties?._checkoutPageDiscountCodes || "";
    const existingOrderCodes =
      item.properties?._checkoutPageOrderDiscounts || "";

    debugLineProperties.push({
      title: item.title,
      key: lineKey,
      isHolder: lineKey === holderKey,
      _checkoutPageDiscountCodes: itemValuationString,
      _checkoutPageOrderDiscounts: desiredOrderCodes,
    });

    if (
      existingLineCodes !== itemValuationString ||
      existingOrderCodes !== desiredOrderCodes
    ) {
      const preservedProperties = { ...item.properties };
      preservedProperties._checkoutPageDiscountCodes = itemValuationString;
      preservedProperties._checkoutPageOrderDiscounts = desiredOrderCodes;

      // /cart/change.js updates properties for each line item individually
      changePromises.push(
        fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: lineKey,
            quantity: item.quantity,
            properties: preservedProperties,
          }),
        }),
      );
    }
  }

  if (changePromises.length > 0) {
    console.log(
      `[Cart Sync] Syncing overlay values for ${changePromises.length} line item(s)...`,
    );
    const responses = await Promise.all(changePromises);
    const unexpectedFailure = responses.some(
      (res) => !res.ok && res.status !== 400 && res.status !== 409,
    );
    const raceFailure = responses.some(
      (res) => !res.ok && (res.status === 400 || res.status === 409),
    );
    console.log("[Cart Sync] Line properties after sync:", debugLineProperties);
    if (raceFailure) {
      if (attempt < MAX_RACE_RETRIES) {
        console.warn(
          `[Cart Sync] Skipped syncing some line(s) due to a concurrent cart write; retrying (attempt ${attempt + 1}/${MAX_RACE_RETRIES})...`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, RACE_RETRY_DELAY_MS),
        );
        // Always fetch fresh here - the snapshot that produced this failure
        // is stale by definition (something else just wrote to the cart).
        return syncCartDiscountPropertiesOnce(null, attempt + 1);
      }
      console.warn(
        "[Cart Sync] Still racing a concurrent cart write after all retries; deferring to the next cart:updated.",
      );
      cartDiscountSyncRerunQueued = true;
    }
    if (unexpectedFailure) {
      throw new Error("Failed to synchronize cart properties.");
    }
  } else {
    console.log(
      "[Cart Sync] No line changes needed. Line properties:",
      debugLineProperties,
    );
  }
}

const DISCOUNT_SYNC_DEBOUNCE_MS = 300;
let discountSyncDebounceTimer = null;

// Debounced so a burst of cart mutations (rapid qty clicks, a freebie app
// chaining its own updates) settles into a single sync against the final
// cart state instead of one sync per intermediate event.
function scheduleCartDiscountSync(event) {
  clearTimeout(discountSyncDebounceTimer);
  const cartOverride = event?.detail?.cart;
  discountSyncDebounceTimer = setTimeout(() => {
    syncCartDiscountProperties(cartOverride);
  }, DISCOUNT_SYNC_DEBOUNCE_MS);
}

document.addEventListener("cart:updated", scheduleCartDiscountSync);
document.addEventListener("cart:refresh", scheduleCartDiscountSync);
